// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Design Tokens com suporte Dark/Light
// ═══════════════════════════════════════════════════════════════════

// Asfalto e sinalização: neutros grafite sem o azul de antes, vermelho Gamma
// como sinal. Os tons de destaque mudam por tema para manter o contraste:
// o ciano que lê bem no escuro some no branco (#216).
const PALETAS = {
  dark: {
    bg: "#0f0f11", surface: "#151518", card: "#1b1b1f", cardHov: "#222227",
    header: "#151518", border: "#2a2a30", border2: "#38383f", dim: "#2a2a30",
    red: "#E82234", gray: "#8b8b93", green: "#22c55e", amber: "#f59e0b",
    purple: "#a78bfa", cyan: "#22d3ee", pink: "#f472b6",
    // muted ≥ 4,5:1 sobre card: é a cor de quase todo rótulo pequeno.
    text: "#f4f4f1", sub: "#b0b0b7", muted: "#8b8b93",
  },
  light: {
    bg: "#f3f2ee", surface: "#ffffff", card: "#ffffff", cardHov: "#f8f7f3",
    // O topo era #050810 também no claro, com texto #111827: contraste 1,1:1.
    header: "#ffffff", border: "#e2e0da", border2: "#cfccc4", dim: "#edece7",
    red: "#C8102E", gray: "#6b6b72", green: "#15803d", amber: "#b45309",
    purple: "#6d28d9", cyan: "#0e7490", pink: "#be185d",
    text: "#18181b", sub: "#3f3f46", muted: "#63636b",
  },
};

// T guarda o HEX do tema ativo, não "var(--t-x)". Uns 370 pontos do código
// fazem T.red + "18" para obter a cor translúcida, e "var(--t-red)18" é CSS
// inválido que o navegador descarta calado: hover, item ativo do menu e
// fundo de etiqueta não apareciam (#216). Com HEX a concatenação vale.
// O preço: T muda por mutação, então quem o lê tem de render de novo na
// troca de tema (App chama aplicarTema no render) e constante fora de
// componente precisa ler T na hora (getter), não no carregamento.
export const T = {};

export function aplicarTema(tema) {
  const p = PALETAS[tema] || PALETAS.dark;
  Object.assign(T, p, {
    blue: p.red, // alias → red
    blueLt: p.red + "12",
    greenLt: p.green + "12",
  });
}

function temaSalvo() {
  try { return localStorage.getItem("tms_tema") || "dark"; } catch { return "dark"; }
}
aplicarTema(temaSalvo());

// Archivo tem eixo de largura. O font-size-adjust do body põe a altura do
// "x" em 0,58 do tamanho (Segoe: 0,50), e o font-stretch estreita de volta:
// a letra fica ~16% mais alta e a linha ocupa a mesma largura de antes.
// Medido em 10px: largura 0,98 da Segoe. Assim os ~900 textos de 8 a 11px
// crescem sem empurrar nenhuma tabela (#216).
const SANS = "'Archivo','Segoe UI',system-ui,sans-serif";

// Não é mais monoespaçada: número em tabela pede algarismo de largura fixa,
// não fonte de código. tabular-nums alinha as colunas e mantém a mesma
// família. O nome ficou para não mexer nas centenas de usos.
export const mono = {
  fontFamily: SANS,
  fontVariantNumeric: "tabular-nums",
};
// Idem: o nome vem da fonte Syne, que nunca foi carregada.
export const syne = {
  fontFamily: SANS,
};
// Títulos em largura expandida: a identidade de placa de estrada. Usar em
// título de tela e de janela, não em texto corrido.
export const expandida = {
  fontFamily: SANS,
  fontStretch: "118%",
  fontWeight: 800,
  letterSpacing: "-.01em",
};

const vars = p => Object.entries({ ...p, redLt: p.red + "12", greenLt: p.green + "12" })
  .map(([k, v]) => `--t-${k}:${v};`).join("");

const G = `
  /* color-scheme: calendário do campo de data, lista do <select> e barra
     de rolagem nativos seguem o tema. Sem isto ficavam claros no escuro. */
  :root, [data-theme="dark"] { color-scheme:dark; ${vars(PALETAS.dark)} }
  [data-theme="light"] { color-scheme:light; ${vars(PALETAS.light)} }

  /* ─── Reset e base ──────────────────────────────────────────── */
  *{box-sizing:border-box;margin:0;padding:0;-webkit-font-smoothing:antialiased}
  html,body{height:100%;overflow:hidden}
  body,input,select,textarea,button{font-family:${SANS};font-size-adjust:.58;font-stretch:84%}
  body{background:var(--t-bg);color:var(--t-text);font-size:13px;line-height:1.5;transition:background .2s,color .2s}
  /* Foco de teclado (#94). O projeto é todo estilo em linha, e estilo em
     linha não alcança :focus-visible — por isso a regra vive aqui, uma vez,
     valendo para botão, campo, link e qualquer coisa com tabindex. Só
     aparece na navegação por teclado: o clique de mouse não desenha nada.
     !important porque 32 lugares do src zeram o contorno com outline:"none"
     em estilo em linha, e estilo em linha vence folha de estilo sem ele. */
  :focus-visible{outline:2px solid var(--t-red)!important;outline-offset:2px}
  /* 3px não se pegava com o mouse. 8px com borda transparente: a barra
     parece fina e a área de pegar é larga. */
  ::-webkit-scrollbar{width:8px;height:8px}
  ::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:var(--t-border2);border-radius:8px;border:2px solid transparent;background-clip:padding-box}
  ::-webkit-scrollbar-thumb:hover{background:var(--t-gray)}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}
  .pulse{animation:pulse 2s ease infinite}
  tr:hover td{background:var(--t-cardHov)!important}
  ::selection{background:#E8223330}

  [data-theme="light"] table{border-collapse:collapse}
  /* ═══ Movimento ══════════════════════════════════════════════════
     Ponderacao: Emil Kowalski primario (restricao e velocidade), Jakub
     Krehel secundario (polimento sutil) — o mapa da skill de motion para
     SaaS dashboard. Referencia: github.com/kylezantos/design-motion-principles

     Duracoes ficam sob 300ms de proposito. Emil: "UI animations should
     generally stay under 300ms", e 180ms responde melhor que 400ms num
     sistema que o operador usa o dia inteiro.

     Nenhuma curva "ease" pura: ela nao tem forca. Toda transicao usa
     Bezier custom.
     ────────────────────────────────────────────────────────────────── */
  :root{
    /* Menor elemento, menor duracao. Nao existe duracao unica. */
    --m-rapido: 120ms;   /* micro: hover, press, troca de icone   */
    --m-base:   180ms;   /* padrao: entrada de elemento, dropdown */
    --m-amplo:  260ms;   /* superficie grande: modal, painel      */

    /* Chegando: desaceleracao forte, quase parando no fim.           */
    --m-saida-suave: cubic-bezier(0.16, 1, 0.3, 1);
    /* Partindo: comeca devagar e acelera para fora.                  */
    --m-entrada-seca: cubic-bezier(0.4, 0, 1, 1);
    /* Mudando de estado sem sair da tela.                            */
    --m-estado: cubic-bezier(0.65, 0, 0.35, 1);
    /* Curva nativa do iOS — usada pelo Vaul do Emil em sheets.       */
    --m-ios: cubic-bezier(0.32, 0.72, 0, 1);

    /* Prontas para o estilo em linha: transition:"var(--m-cor)". Lista as
       propriedades em vez de "all", que animava ate o que nao devia. */
    --m-cor: color var(--m-rapido) var(--m-estado),
             background-color var(--m-rapido) var(--m-estado),
             border-color var(--m-rapido) var(--m-estado),
             box-shadow var(--m-rapido) var(--m-estado),
             opacity var(--m-rapido) var(--m-estado),
             transform var(--m-rapido) var(--m-estado);
    /* Barra de progresso: cresce uma vez quando o dado chega. */
    --m-barra: width var(--m-amplo) var(--m-saida-suave),
               height var(--m-amplo) var(--m-saida-suave);
  }

  /* Entrada (receita do Jakub): opacidade + deslocamento + desfoque.
     O blur faz o elemento "materializar" em vez de so aparecer — mais
     fisico que fade puro. */
  @keyframes mEntra{
    from{opacity:0;transform:translateY(6px);filter:blur(4px)}
    to  {opacity:1;transform:none;         filter:blur(0)}
  }
  /* Saida e SEMPRE mais sutil que a entrada: menos deslocamento, porque
     a atencao do usuario ja foi para o que vem depois. */
  @keyframes mSai{
    from{opacity:1;transform:none;          filter:blur(0)}
    to  {opacity:0;transform:translateY(-3px);filter:blur(2px)}
  }
  .fu     {animation:mEntra var(--m-base) var(--m-saida-suave) both}
  .fu-out {animation:mSai   var(--m-rapido) var(--m-entrada-seca) both}

  /* Escalonamento para listas curtas. Passos pequenos: 8 itens a 30ms
     somam 240ms, ainda dentro do orcamento. Acima disso vira espera. */
  .fu-1{animation-delay:30ms}  .fu-2{animation-delay:60ms}
  .fu-3{animation-delay:90ms}  .fu-4{animation-delay:120ms}
  .fu-5{animation-delay:150ms} .fu-6{animation-delay:180ms}

  /* ─── Skeleton ───────────────────────────────────────────────────
     O brilho corre SOBRE uma base solida. Se a animacao for congelada
     (reduced motion), sobra a base — o skeleton continua legivel. */
  @keyframes mBrilho{
    from{transform:translateX(-100%)}
    to  {transform:translateX(100%)}
  }
  .sk{
    position:relative;overflow:hidden;
    background:var(--t-border2);
    border-radius:6px;
  }
  .sk::after{
    content:"";position:absolute;inset:0;
    background:linear-gradient(90deg,transparent,var(--t-cardHov),transparent);
    animation:mBrilho 1.4s linear infinite;
  }

  /* ─── Progresso indeterminado ────────────────────────────────────
     linear() de proposito: barra de progresso e movimento continuo, e
     a skill indica linear justamente para loop continuo. */
  @keyframes mBarra{
    from{transform:translateX(-100%)}
    to  {transform:translateX(400%)}
  }
  /* Spinner. Rotacao continua tambem e linear. */
  @keyframes mGira{to{transform:rotate(360deg)}}

  /* ─── Superficies (modal, painel) ────────────────────────────────
     Nao parte de scale(0): produz movimento anti-natural. 0.97 e o piso. */
  @keyframes mSurge{
    from{opacity:0;transform:scale(.97) translateY(4px);filter:blur(3px)}
    to  {opacity:1;transform:none;                      filter:blur(0)}
  }
  .m-surge{animation:mSurge var(--m-amplo) var(--m-ios) both}
  /* Fundo escuro do modal: so opacidade, sem deslocar. */
  @keyframes mAparece{from{opacity:0}}
  .m-fundo{animation:mAparece var(--m-base) var(--m-saida-suave) both}

  /* Feedback tatil: o botao afunda no clique. Emil, dica 1.
     Fica em :active — e resposta ao toque, nao decoracao. */
  .m-toque{transition:transform var(--m-rapido) var(--m-estado)}
  .m-toque:active{transform:scale(.97)}

  /* ═══ Acessibilidade — NAO e opcional ════════════════════════════
     Desliga o movimento preservando o estado final, para nenhum layout
     quebrar. Vale para tudo, inclusive .pulse e .glow. */
  @media (prefers-reduced-motion: reduce){
    *,*::before,*::after{
      animation-duration:.01ms!important;
      animation-iteration-count:1!important;
      transition-duration:.01ms!important;
      scroll-behavior:auto!important;
    }
  }

`;

export const GLOBAL_CSS = G;
